import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { SgSelectOption, SgSelectOptionGroup } from '../../models';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { UnRx } from '@pebula/utils';
import { MatFormFieldAppearance, SubscriptSizing } from '@angular/material/form-field';

@Component({
  selector: 'sg-dropdown',
  templateUrl: './sg-dropdown.component.html',
  styleUrls: ['./sg-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgDropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SgDropdownComponent implements ControlValueAccessor, OnDestroy {
  @Input()
  get items(): SgSelectOptionGroup[] | SgSelectOption[] {
    return this._items;
  }
  set items(value: SgSelectOptionGroup[] | SgSelectOption[]) {
    this._items = value ?? [];
    // If writeValue ran before items were provided, resolve the pending value now.
    if (this._pendingValue !== undefined && this._items.length > 0) {
      if (this.isMultiSelect) {
        const opts = this._getOptionsFromValues(this._pendingValue);
        this.selectedOptions.setValue(opts, { emitEvent: false });
      } else {
        const opt = this._getOptionFromValue(this._pendingValue);
        this.selectedOption.setValue(opt, { emitEvent: false });
      }
      this._pendingValue = undefined;
      this.cdr.markForCheck();
    }
    this._scheduleMatSelectRecheck();
  }
  private _items: SgSelectOptionGroup[] | SgSelectOption[] = [];
  private _pendingValue: any = undefined;
  private _pendingRecheckTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() label = '';
  @Input() placeholder = '';
  @Input() labelIcon = '';
  @Input() icon = '';
  @Input() iconPosition: 'start' | 'end' = 'start';
  @Input() isMultiSelect = false;
  @Input() isGrouped = false;
  @Input() showGroupDivider = false;
  @Input() hint = '';
  @Input() size: 'default' | 'small' | 'x-small' = 'default';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() errorText = '';
  @Input() subscriptSizing: SubscriptSizing = 'fixed';
  @Input() panelClass: string | string[] = 'sg-drop-down-select';

  @ViewChild(MatSelect) matSelect: MatSelect;

  @HostBinding('class.sg-dropdown-disabled')
  isDisabled = false;

  selectedOptions = new FormControl<SgSelectOption[]>([]);
  selectedOption = new FormControl<SgSelectOption>(null);
  isOpen = false;

  get panelClasses(): string[] {
    const baseClass = 'sg-drop-down-select';
    const classes = Array.isArray(this.panelClass) ? this.panelClass : [this.panelClass];

    // Ensure base class is always first, then add other unique classes
    const uniqueClasses = [baseClass, ...classes.filter((c) => c && c !== baseClass)];
    return uniqueClasses;
  }

  get normalizedItems(): SgSelectOptionGroup[] {
    if (!this.items || this.items.length === 0) {
      return [];
    }

    if (this._isOptionArray(this.items)) {
      return [{ label: '', options: this.items }];
    }

    return this.items;
  }

  private _onChange: any = () => {};
  private _onTouched: any = () => {};

  private _isOptionArray(items: SgSelectOptionGroup[] | SgSelectOption[]): items is SgSelectOption[] {
    return items.length > 0 && 'value' in items[0];
  }

  writeValue(obj: any): void {
    // Items may arrive after the form value (async parent data). Stash the
    // raw value so the items setter can resolve it once options exist.
    if (this.isMultiSelect) {
      const selectedOptions = this._getOptionsFromValues(obj || []);
      if (Array.isArray(obj) && obj.length > 0 && selectedOptions.length === 0 && (this._items?.length ?? 0) === 0) {
        this._pendingValue = obj;
      }
      this.selectedOptions.setValue(selectedOptions, { emitEvent: false });
    } else {
      const selectedOption = this._getOptionFromValue(obj);
      if (obj !== null && obj !== undefined && selectedOption === null && (this._items?.length ?? 0) === 0) {
        this._pendingValue = obj;
      }
      this.selectedOption.setValue(selectedOption, { emitEvent: false });
    }
    this.cdr.markForCheck();
    this._scheduleMatSelectRecheck();
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    if (this._pendingRecheckTimer !== null) {
      clearTimeout(this._pendingRecheckTimer);
      this._pendingRecheckTimer = null;
    }
  }

  /**
   * Force the inner <mat-select> to re-resolve its selection. Under Material 21
   * (MDC), mat-options live inside the cdk-connected-overlay panel template;
   * if mat-select's first writeValue ran before compareWith could match an
   * option, its internal `_assignValue` short-circuits all subsequent calls
   * with the same value reference — leaving selectionModel empty and the
   * mat-select-trigger projection suppressed. Writing `null` then the wrapper
   * resets `_value` so `_setSelectionByValue` runs again against current options.
   *
   * Duplicate schedules (e.g. writeValue + items setter back-to-back) collapse
   * into a single timer, and the timer is cleared on destroy so we don't poke
   * a torn-down mat-select.
   */
  private _scheduleMatSelectRecheck(): void {
    if (this._pendingRecheckTimer !== null) {
      clearTimeout(this._pendingRecheckTimer);
    }
    this._pendingRecheckTimer = setTimeout(() => {
      this._pendingRecheckTimer = null;
      if (this._destroyed) return;
      const matSelect = this.matSelect;
      if (!matSelect) return;
      const value = this.isMultiSelect ? this.selectedOptions.value : this.selectedOption.value;
      const hasValue = Array.isArray(value) ? value.length > 0 : value != null;
      if (hasValue) {
        matSelect.writeValue(null);
        matSelect.writeValue(value);
      }
    });
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;

    this.selectedOptions?.valueChanges?.pipe(UnRx(this)).subscribe((options: SgSelectOption[]) => {
      if (this.isMultiSelect) {
        // Emit array of values, not the full option objects
        const values = options?.map((opt) => opt.value) || [];
        this._onChange(values);
      }
    });

    this.selectedOption?.valueChanges?.pipe(UnRx(this)).subscribe((option: SgSelectOption) => {
      if (!this.isMultiSelect) {
        // Emit just the value, not the full option object
        this._onChange(option?.value);
      }
    });
  }

  private _getOptionFromValue(value: any): SgSelectOption | null {
    if (value === null || value === undefined) {
      return null;
    }

    // If it's already an option object, return it
    if (typeof value === 'object' && 'value' in value && 'label' in value) {
      return value;
    }

    // Find the option by value
    for (const group of this.normalizedItems) {
      const option = group.options.find((opt) => opt.value === value);
      if (option) {
        return option;
      }
    }

    return null;
  }

  private _getOptionsFromValues(values: any[]): SgSelectOption[] {
    if (!values || values.length === 0) {
      return [];
    }

    // If already option objects, return them
    if (typeof values[0] === 'object' && 'value' in values[0] && 'label' in values[0]) {
      return values;
    }

    // Map values to option objects
    const options: SgSelectOption[] = [];
    for (const value of values) {
      for (const group of this.normalizedItems) {
        const option = group.options.find((opt) => opt.value === value);
        if (option) {
          options.push(option);
          break;
        }
      }
    }

    return options;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled && this.selectedOptions.enabled && this.selectedOption.enabled) {
      this.selectedOptions.disable();
      this.selectedOption.disable();
    } else if (!isDisabled && this.selectedOptions.disabled && this.selectedOption.disabled) {
      this.selectedOptions.enable();
      this.selectedOption.enable();
    }
  }

  isSelected(option: SgSelectOption): boolean {
    if (this.isMultiSelect) {
      return this.selectedOptions.value.some((selectedOption: SgSelectOption) => selectedOption.value === option.value);
    } else {
      return this.selectedOption.value?.value === option.value;
    }
  }

  deselectOptions(optionsToRemove: SgSelectOption[]) {
    if (this.matSelect) {
      this.matSelect.options.forEach((option) => {
        if (optionsToRemove.some((optionToRemove: SgSelectOption) => option.value?.value === optionToRemove.value)) {
          option.deselect();
        }
      });
    }
  }

  hasError(): boolean {
    return (
      (this.selectedOptions.invalid && this.selectedOptions.touched) ||
      (this.selectedOption.invalid && this.selectedOptions.touched)
    );
  }

  onOpenChange(isOpen: boolean): void {
    this.isOpen = isOpen;
  }

  get totalOptions(): number {
    if (this._isOptionArray(this.items)) {
      return this.items.length;
    }

    return this.items.reduce((total, group) => total + (group.options?.length || 0), 0);
  }

  compareOptions(o1: SgSelectOption, o2: SgSelectOption): boolean {
    return o1 && o2 ? o1.value === o2.value : o1 === o2;
  }

  getOptionDataTest(option: SgSelectOption): string | null {
    if (!option) {
      return null;
    }

    const valueType = typeof option.value;
    const isPrimitive =
      option.value != null && (valueType === 'string' || valueType === 'number' || valueType === 'boolean');
    const rawValue = option.optionTestId || (isPrimitive ? `option-${option.value}` : '');

    return rawValue ? rawValue.replace(/[^a-zA-Z0-9-_]/g, '-') : null;
  }
}
